import React, { useEffect, useState, useCallback, useRef } from "react";

import { compareAsc, parseISO } from "date-fns";
import { debounce } from "lodash";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import { usePageTitle } from "@/atoms/page-title-atom";
import { type ApolloQueryResult } from "@apollo/client";
import { useUser } from "@components/user-context";
import {
  DocumentsTable,
  DocumentsHeader,
} from "@features/documents/components";
import { FETCH_DOCUMENTS } from "@gql/documents";
import {
  type DocumentsVariables,
  type Documents,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { type PaginationState } from "@north-beam/nb-common/pagination";
import { TextInput } from "@shared/text-input";

type Doc = Documents["me"]["documents"]["data"][number];
type ContentTypes = Record<string, string>;

type Data = {
  docs: Doc[];
  contentTypes: ContentTypes;
};

const PAGE_SIZE = 20;

export const DocumentsPage = () => {
  const initialDataLoaded = useRef<boolean>(false);

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 1), {
    updateType: "pushIn",
  });
  const [urlSearch, setUrlSearch] = useQueryParam(
    "search",
    withDefault(StringParam, ""),
    {
      updateType: "pushIn",
    },
  );
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState(urlSearch);
  const debouncedSetState = React.useMemo(
    () => debounce(setUrlSearch, 200),
    [setUrlSearch],
  );

  useEffect(() => {
    debouncedSetState(search);
  }, [search, debouncedSetState]);

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState<Data | null>(null);
  const [isManuallyRefreshing, setIsManuallyRefreshing] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);

  const [, setPageTitle] = usePageTitle();
  useEffect(() => {
    setPageTitle("Documents");
  }, [setPageTitle]);

  const {
    user: { apolloClient },
  } = useUser();

  const requestDocuments = useCallback(
    async (pagination: PaginationState, options?: { search?: string }) => {
      let result: ApolloQueryResult<Documents> | null = null;
      try {
        result = await apolloClient.query<Documents, DocumentsVariables>({
          query: FETCH_DOCUMENTS,
          variables: { pagination, search: options?.search },
          fetchPolicy: "no-cache",
        });
      } catch (e) {
        setError(true);
        setData(null);
        return false;
      }
      setError(false);
      setCount(result.data.me.documents.count);
      setData({
        docs: result.data.me.documents.data,
        contentTypes: result.data.me.documentContentTypes,
      });
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [apolloClient],
  );

  const loadInitialData = useCallback(async () => {
    if (initialDataLoaded.current === false) {
      setLoading(true);
      await requestDocuments({ page, pageSize: PAGE_SIZE }, { search });
      initialDataLoaded.current = true;
      setLoading(false);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [requestDocuments, search]);

  useEffect(() => {
    const performRefetch = async () => {
      setIsRefetching(true);
      await requestDocuments({ page, pageSize: PAGE_SIZE }, { search });
      setIsRefetching(false);
    };
    if (initialDataLoaded.current === true) {
      performRefetch();
    }
  }, [page, requestDocuments, search]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (data) {
      const expirations: Date[] = data.docs.map(({ link }) =>
        parseISO(link.expiresAt),
      );
      const sortedExpirations = expirations.sort((first, second) =>
        compareAsc(first, second),
      );
      const tenMinutesFromNow = Date.now() + 10 * 60 * 1000;

      const minimumExpiration =
        sortedExpirations[0]?.getTime() || tenMinutesFromNow;

      const timeoutHandle = setTimeout(() => {
        loadInitialData();
      }, minimumExpiration - Date.now());

      return () => {
        clearTimeout(timeoutHandle);
      };
    }
  }, [data, loadInitialData]);

  return (
    <div className="p-4 min-h-full flex flex-col items-center max-h-full">
      <div
        className={
          "bg-white px-7 p-5 rounded-md flex flex-col gap-3 container min-h-full h-full grow"
        }
      >
        <div className="flex flex-col grow relative grow overflow-y-hidden max-h-full">
          <div className="flex flex-col gap-3">
            <DocumentsHeader
              isDisabled={error === true}
              isRefreshing={isManuallyRefreshing}
              onRefresh={async () => {
                setIsManuallyRefreshing(true);
                await requestDocuments(
                  { page, pageSize: PAGE_SIZE },
                  { search },
                );
                setIsManuallyRefreshing(false);
              }}
              onUploadSuccess={async () => {
                setIsRefetching(true);
                await requestDocuments(
                  { page, pageSize: PAGE_SIZE },
                  { search },
                );
                setIsRefetching(false);
              }}
            />
            <hr className="mb-0 mt-0" />
          </div>
          <TextInput
            value={search}
            label="SEARCH BY NAME"
            className="m-2 mb-3 w-[30rem]"
            placeholder="sales_data..."
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
          <DocumentsTable
            data={data}
            isRefetching={isRefetching}
            error={error}
            isInitialDataLoading={isLoading}
            page={page}
            pageSize={PAGE_SIZE}
            count={count}
            onPaginationChange={(val) => setPage(val)}
            onDeleteSuccess={async () => {
              setIsRefetching(true);
              await requestDocuments({ page, pageSize: PAGE_SIZE }, { search });
              setIsRefetching(false);
            }}
          />
        </div>
      </div>
    </div>
  );
};
